import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Inbox as InboxIcon, Users, KanbanSquare, Megaphone,
  Workflow, BarChart3, Sparkles, MessageSquare, LogOut, Menu, Search, Bell, CreditCard, Plug, ShieldCheck, Shield
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "dashboard-sidebar-home" },
  { to: "/app/inbox", label: "Team Inbox", icon: InboxIcon, testid: "dashboard-sidebar-inbox" },
  { to: "/app/crm", label: "CRM", icon: Users, testid: "dashboard-sidebar-crm" },
  { to: "/app/pipeline", label: "Sales Pipeline", icon: KanbanSquare, testid: "dashboard-sidebar-pipeline" },
  { to: "/app/broadcast", label: "Broadcast", icon: Megaphone, testid: "dashboard-sidebar-broadcast" },
  { to: "/app/automation", label: "Automation", icon: Workflow, testid: "dashboard-sidebar-automation" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "dashboard-sidebar-analytics" },
  { to: "/app/assistant", label: "AI Assistant", icon: Sparkles, testid: "dashboard-sidebar-assistant" },
  { to: "/app/connect", label: "Connect Channels", icon: Plug, testid: "dashboard-sidebar-connect" },
  { to: "/app/meta-center", label: "Meta Center", icon: ShieldCheck, testid: "dashboard-sidebar-metacenter" },
  { to: "/app/billing", label: "Langganan", icon: CreditCard, testid: "dashboard-sidebar-billing" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 shrink-0 border-r border-border bg-card flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="relative h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-primary-foreground" />
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-400 border-2 border-card" />
          </div>
          <span className="font-display font-bold tracking-tight">WasapMe</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
          {user?.super_admin && (
            <NavLink to="/app/admin" data-testid="dashboard-sidebar-admin" onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Shield className="h-[18px] w-[18px]" /> Platform Admin
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="rounded-xl bg-accent/60 p-3 text-xs">
            <p className="font-semibold text-accent-foreground">Growth Plan</p>
            <p className="text-muted-foreground mt-0.5">3,240 / 5,000 AI conversations</p>
            <div className="mt-2 h-1.5 rounded-full bg-primary/20 overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: "65%" }} />
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-md flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu className="h-5 w-5" /></button>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="global-search-input" placeholder="Cari pelanggan, chat, kempen..."
              className="w-full h-9 pl-9 pr-3 rounded-full bg-secondary text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button className="relative h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center" data-testid="notifications-button">
            <Bell className="h-4 w-4" />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-rose-600">
                <LogOut className="h-4 w-4 mr-2" /> Log Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 min-w-0 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
